import { GoogleGenAI, HarmCategory, HarmBlockThreshold } from "@google/genai";
import { getStorage, ref, getBlob } from 'firebase/storage';

// Initialize client with the key. The API key must be obtained exclusively from the environment variable process.env.API_KEY.
const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY });

export type ImageStyle =
  | 'photorealistic'
  | 'cinematic'
  | 'oil_painting'
  | 'sketch'
  | 'watercolor'
  | 'digital_art'
  | 'anime'
  | 'renaissance'      // Classical religious art style
  | 'stained_glass'    // Church window aesthetic
  | 'illustration'     // Clean book illustration
  | 'ancient_mosaic'   // Byzantine/early Christian
  | 'warm_pastoral';   // Peaceful pastoral scenes
export type AspectRatio = '1:1' | '16:9' | '9:16' | '4:3' | '3:4';

export interface ImageGenerationOptions {
  prompt: string;
  style?: ImageStyle;
  aspectRatio?: AspectRatio;
}

export type VideoStyle = 'cinematic' | 'animation' | 'natural' | 'vintage' | 'documentary' | 'dramatic';
export type VideoResolution = '720p' | '1080p';
export type VideoDuration = 4 | 6 | 8;
export type VideoAspectRatio = '16:9' | '9:16';

export interface VideoGenerationOptions {
  prompt: string;
  imageInput?: string;
  style?: VideoStyle;
  resolution?: VideoResolution;
  duration?: VideoDuration;
  aspectRatio?: VideoAspectRatio;
  negativePrompt?: string;
}

// Helper function to extract Firebase Storage path from URL
const extractFirebaseStoragePath = (url: string): string | null => {
  try {
    // Example URL: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media&token=...
    const match = url.match(/\/o\/([^?]+)/);
    if (match && match[1]) {
      // Decode the URL-encoded path
      return decodeURIComponent(match[1]);
    }
  } catch (e) {
    console.warn("Failed to extract Firebase Storage path from URL", e);
  }
  return null;
};

const SAFETY_PROMPT_SUFFIX = `
CRITICAL SAFETY GUIDELINES (NON-NEGOTIABLE):
1. The content MUST be suitable for a general audience including families and children.
2. ABSOLUTELY NO nudity, sexual content, or sexually suggestive themes.
3. NO graphic violence, gore, or disturbing imagery.
4. Maintain a respectful and dignified tone appropriate for a Christian Bible study application.
5. If the user prompt violates these guidelines, generate a respectful, safe, and generic alternative or refuse.
6. DO NOT include any text, words, letters, numbers, symbols, signs, or writing of any kind in the image. The image must be purely visual with no textual elements whatsoever.
`;

export const generateBibleText = async (prompt: string, currentText: string): Promise<string> => {
  const fullPrompt = `Task: Rewrite the following text based on the instructions.

  Original Text: "${currentText}"

Instructions: ${prompt}

${SAFETY_PROMPT_SUFFIX}

Return ONLY the rewritten text.Do not include quotes or conversational filler.`;

  try {
    const result = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        {
          role: 'user',
          parts: [
            {
              text: fullPrompt,
            },
          ],
        },
      ],
      config: {
        maxOutputTokens: 1000,
      }
    });

    if (result.candidates?.[0]?.content?.parts?.[0]?.text) {
      return result.candidates[0].content.parts[0].text;
    }

    throw new Error("No text returned from Gemini.");
  } catch (error) {
    console.error("Gemini Text Generation Error:", error);
    throw error;
  }
};


export const generateBibleImage = async (options: string | ImageGenerationOptions): Promise<string> => {
  const prompt = typeof options === 'string' ? options : options.prompt;
  const style = typeof options === 'string' ? 'cinematic' : (options.style || 'cinematic');
  const aspectRatio = typeof options === 'string' ? '16:9' : (options.aspectRatio || '16:9');

  // Construct the full prompt with style and safety constraints
  const fullPrompt = `Create a ${style} image depicting: ${prompt}.
  High quality, detailed.No text, no words, no letters, no symbols, no writing of any language.
  ${SAFETY_PROMPT_SUFFIX} `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [
          {
            text: fullPrompt,
          },
        ],
      },
      config: {
        responseModalities: ['IMAGE'],
        imageConfig: {
          aspectRatio: aspectRatio
        },
        // Strict safety settings - block at lowest threshold
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE
          },
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE
          }
        ]
      }
    });

    // Extract image
    if (response.candidates?.[0]?.content?.parts) {
      for (const part of response.candidates[0].content.parts) {
        if (part.inlineData && part.inlineData.data) {
          return `data:${part.inlineData.mimeType}; base64, ${part.inlineData.data} `;
        }
      }
    }

    throw new Error("No image data returned from Gemini.");
  } catch (error) {
    console.error("Gemini Generation Error:", error);
    throw error;
  }
};

export const generateBibleVideo = async (options: string | VideoGenerationOptions, imageInputDeprecated?: string): Promise<string> => {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  const model = 'veo-3.1-fast-generate-preview';
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:predictLongRunning?key=${apiKey}`;

  // Handle both old signature (prompt, imageInput) and new signature (options object)
  let prompt: string;
  let imageInput: string | undefined;
  let style: VideoStyle = 'cinematic';
  let resolution: VideoResolution = '1080p';
  let duration: VideoDuration = 8;
  let aspectRatio: VideoAspectRatio = '16:9';
  let negativePrompt: string | undefined;

  if (typeof options === 'string') {
    prompt = options;
    imageInput = imageInputDeprecated;
  } else {
    prompt = options.prompt;
    imageInput = options.imageInput;
    style = options.style || 'cinematic';
    resolution = options.resolution || '1080p';
    duration = options.duration || 8;
    aspectRatio = options.aspectRatio || '16:9';
    negativePrompt = options.negativePrompt;
  }

  // Build the prompt with style guidance
  const fullPrompt = `Create a ${style}, respectful, and high-quality video suitable for a Bible study depicting: ${prompt}.
  Style: ${style}.
  ${SAFETY_PROMPT_SUFFIX}`;

  try {
    console.log(`Starting video generation (LRO) with model: ${model}`);

    // Construct Payload
    // Using camelCase as hinted by initial error messages
    const instance: any = {
      prompt: fullPrompt
    };

    if (imageInput) {
      let finalBase64 = imageInput;
      let mimeType = 'image/jpeg';

      // If it is a URL, fetch and convert to base64
      if (imageInput.startsWith('http')) {
        try {
          let blob: Blob;

          // Check if it's a Firebase Storage URL
          const firebasePath = extractFirebaseStoragePath(imageInput);
          if (firebasePath) {
            // Use Firebase Storage SDK to download the file
            console.log('Downloading Firebase Storage file:', firebasePath);
            const storage = getStorage();
            const fileRef = ref(storage, firebasePath);
            blob = await getBlob(fileRef);
          } else {
            // For non-Firebase URLs, use regular fetch
            const imgRes = await fetch(imageInput);
            blob = await imgRes.blob();
          }

          mimeType = blob.type || 'image/jpeg';
          finalBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          console.error("Failed to fetch image from URL for video generation:", err);
          throw new Error(`Failed to download reference image: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      } else if (imageInput.startsWith('data:')) {
        const matches = imageInput.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
        if (matches && matches[1]) {
          mimeType = matches[1];
        }
      }

      // Stripping data URI prefix and ensuring clean base64
      const cleanBase64 = finalBase64.split(',')[1] || finalBase64;
      instance.image = {
        bytesBase64Encoded: cleanBase64,
        mimeType: mimeType
      };
    }

    // Build parameters object with actual Veo 3.1 API parameters
    // Note: generateAudio is not supported by veo-3.1-fast-generate-preview
    const parameters: any = {
      sampleCount: 1,
      durationSeconds: duration,
      aspectRatio: aspectRatio
    };

    // Resolution: 1080p only available with 8s duration
    if (resolution === '1080p' && duration === 8) {
      parameters.resolution = '1080p';
    } else if (resolution === '1080p' && duration !== 8) {
      // Fall back to 720p if user wants 1080p but duration isn't 8s
      parameters.resolution = '720p';
      console.warn('1080p resolution requires 8s duration. Falling back to 720p.');
    } else {
      parameters.resolution = resolution;
    }

    // Optional negative prompt
    if (negativePrompt) {
      parameters.negativePrompt = negativePrompt;
    }

    // 1. Start Long Running Operation
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        instances: [instance],
        parameters
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Video Generation Start Failed:", errorText);
      throw new Error(`Video generation start failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const initialResult = await response.json();
    console.log("LRO Started:", initialResult);

    // Get Operation Name
    // Response format: { "name": "projects/.../operations/..." }
    const operationName = initialResult.name;
    if (!operationName) {
      // It might be synchronous if it finished fast (unlikely) or different format
      if (initialResult.responses?.[0]?.video?.uri) {
        return initialResult.responses[0].video.uri;
      }
      // Check for error in response
      if (initialResult.error) {
        throw new Error(`Video generation error: ${initialResult.error.message}`);
      }
      throw new Error("No operation name returned for video generation.");
    }

    // 2. Poll for Completion
    const pollUrl = `https://generativelanguage.googleapis.com/v1beta/${operationName}?key=${apiKey}`;

    // Poll max 150 times, every 3 seconds (7.5 minutes max wait)
    // Video generation can take 3-5+ minutes depending on duration and complexity
    const maxPolls = 150;
    const pollInterval = 3000; // 3 seconds

    for (let i = 0; i < maxPolls; i++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      const pollResponse = await fetch(pollUrl);
      if (!pollResponse.ok) {
        console.warn(`Polling attempt ${i + 1}/${maxPolls} failed temporarily:`, await pollResponse.text());
        continue;
      }

      const opResult = await pollResponse.json();

      // Log progress every 10 polls or when done
      if (i % 10 === 0 || opResult.done) {
        const elapsed = ((i + 1) * pollInterval / 1000).toFixed(0);
        console.log(`Video generation poll ${i + 1}/${maxPolls} (${elapsed}s elapsed):`, opResult.done ? 'COMPLETE' : 'in progress...');
      }

      if (opResult.done) {
        if (opResult.error) {
          throw new Error(`Video generation operation failed: ${opResult.error.message}`);
        }

        // Extract Result
        // Format: { done: true, response: { "@type": "...", generatedSamples: [...] } }
        // OR { done: true, response: { "@type": "...", responses: [...] } } (Vertex style)

        // Check for 'response' field which contains the result
        const finalResponse = opResult.response;

        // Check standard Veo output paths
        // 1. generateVideoResponse (Gemini API v1beta wrapper)
        if (finalResponse?.generateVideoResponse?.generatedSamples?.[0]?.video?.uri) {
          const videoUri = finalResponse.generateVideoResponse.generatedSamples[0].video.uri;
          return await downloadAndConvertToBase64(videoUri, apiKey);
        }

        // 2. generatedSamples (Direct)
        if (finalResponse?.generatedSamples?.[0]?.video?.uri) {
          const videoUri = finalResponse.generatedSamples[0].video.uri;
          return await downloadAndConvertToBase64(videoUri, apiKey);
        }

        // 3. responses (Vertex batch predict style)
        if (finalResponse?.responses?.[0]?.video?.uri) {
          const videoUri = finalResponse.responses[0].video.uri;
          return await downloadAndConvertToBase64(videoUri, apiKey);
        }

        // Log the full response for debugging
        console.error("Video generation completed but no video URI found. Full response:", JSON.stringify(finalResponse, null, 2));
        throw new Error("Video generation completed but no video URI was found in the response.");
      }
    }

    throw new Error(`Video generation timed out after ${(maxPolls * pollInterval / 1000 / 60).toFixed(1)} minutes. The video may still be processing - try again later.`);

  } catch (error: any) {
    console.error("Gemini Video Generation Error:", error);
    throw error;
  }
};

// Helper to download video from Google URI and convert to Base64
const downloadAndConvertToBase64 = async (uri: string, apiKey: string): Promise<string> => {
  try {
    // Append API key to the URI
    const separator = uri.includes('?') ? '&' : '?';
    const authUrl = `${uri}${separator}key=${apiKey}`;

    console.log("Downloading generated video...");
    const response = await fetch(authUrl);

    if (!response.ok) {
      throw new Error(`Failed to download video: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result && typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert video blob to base64"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error downloading video:", error);
    throw error;
  }
};

export const generateStudyPlan = async (topic: string, title: string, sectionCount: number = 8): Promise<{ description: string, coverImagePrompt: string, segments: any[] }> => {
  const prompt = `
    Create a detailed Bible study plan on the topic: "${topic}" with the title: "${title}".
    The study should have exactly ${sectionCount} segments.
    Each segment should be substantive.
    Return a JSON object with the following structure:
    {
      "description": "A compelling 2-3 sentence description of the study.",
      "coverImagePrompt": "A cinematic, high-quality fine art description of a cover image for this study. Focus on lighting, mood (atmospheric, ethereal, or dramatic), and symbolic theological elements. NO TEXT, NO DISTORTED FACES, NO GENERIC CLIPART.",
      "segments": [
        {
          "type": "text" | "image-text",
          "content": "The actual text content for the user to read. Make it substantive and educational.",
          "metadata": {
            "imagePrompt": "A detailed, cinematic visual description of an image for this segment. Use a 'fine art photography' or 'classical oil painting' aesthetic. Focus on textures, dramatic lighting (e.g., chiaroscuro), and atmospheric details. Avoid text, modern objects, or generic looks."
          }
        }
      ]
    }

    The segments should be a mix of 'text' (for reading) and 'image-text' (for key concepts with visual aids).
    
    The theological perspective should always be from a Seventh-day Adventist viewpoint (e.g., emphasis on the Sabbath, the sanctuary, the second coming, the state of the dead, etc. where applicable). 
    IMPORTANT: Do NOT mention "Seventh-day Adventist", "Adventist", or use phrases like "from an Adventist perspective". State all theological points as direct, factual truth.

    Ensure the content is theologically sound, engaging, and educational.
    The imagePrompts and coverImagePrompt MUST be purely visual, describing the scene, mood, and elements.
    RETURN ONLY THE JSON OBJECT. NO MARKDOWN FORMATTING.
    ${SAFETY_PROMPT_SUFFIX}
  `;

  try {
    const result = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        {
          role: 'user',
          parts: [{ text: prompt }],
        },
      ],
      config: {
        responseMimeType: 'application/json',
      }
    });

    if (result.candidates?.[0]?.content?.parts?.[0]?.text) {
      let text = result.candidates[0].content.parts[0].text;

      // Extract the JSON part in case the model included extra text or markdown
      const firstBrace = text.indexOf('{');
      const lastBrace = text.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace !== -1) {
        text = text.substring(firstBrace, lastBrace + 1);
      }

      return JSON.parse(text);
    }

    throw new Error("No plan returned from Gemini.");
  } catch (error) {
    console.error("Gemini Study Plan Generation Error:", error);
    throw error;
  }
};
